import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Dialog from 'components/Dialog';
import {
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  CircularProgress,
  IconButton,
  Fab,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import { getNotificationEmails, saveNotificationEmails } from 'Settings/Security/SecFunctions';
import { handleError } from 'reducers/ErrorReducer';
import { connect } from 'react-redux';

const useStyles = makeStyles(theme => ({
  content: {
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  textField: {
    flex: 1,
  },
  list: {
    flex: 1,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 300,
  },
  listItem: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
}));

const NotificationEmailsModal = props => {
  const classes = useStyles();
  const { onClose } = props;
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    loadEmails();
  }, []);

  const loadEmails = async () => {
    try {
      setLoading(true);
      const result = await getNotificationEmails();
      setEmails(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error loading notification emails:', error);
      props.handleError(error, 'Error loading notification emails');
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = email => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = newEmail.trim();

    // Validate email format
    if (!validateEmail(trimmedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    // Check for duplicates
    if (emails.includes(trimmedEmail)) {
      setEmailError('This email is already in the list');
      return;
    }

    // Add email to list
    setEmails([...emails, trimmedEmail]);
    setNewEmail('');
    setEmailError('');
  };

  const handleDeleteEmail = emailToDelete => {
    setEmails(emails.filter(email => email !== emailToDelete));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveNotificationEmails(emails);
      onClose();
    } catch (error) {
      console.error('Error saving notification emails:', error);
      props.handleError(error, 'Error saving notification emails');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      handleAddEmail();
    }
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" variant="contained" onClick={handleSave} disabled={saving}>
          <SaveIcon /> Save
        </Button>
        <Button color="primary" onClick={onClose} disabled={saving}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  return (
    <Dialog
      toolbar
      onClose={onClose}
      title="Disabled Login Notification Emails"
      actions={renderActions()}
      maxWidth="sm"
      fullWidth>
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : (
        <div className={classes.content}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter email addresses to be notified for disabled login attempts
          </Typography>

          <div className={classes.inputContainer}>
            <TextField
              className={classes.textField}
              variant="outlined"
              size="small"
              placeholder="Enter email address"
              value={newEmail}
              onChange={e => {
                setNewEmail(e.target.value);
                setEmailError('');
              }}
              onKeyPress={handleKeyPress}
              error={!!emailError}
              disabled={saving}
            />
            <Fab
              size="small"
              color="secondary"
              onClick={handleAddEmail}
              disabled={!newEmail.trim() || saving}>
              <AddIcon />
            </Fab>
          </div>

          {emailError && <Typography className={classes.errorText}>{emailError}</Typography>}

          {emails.length === 0 ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                No email addresses configured yet. Add an email address above to get started.
              </Typography>
            </div>
          ) : (
            <List className={classes.list}>
              {emails.map((email, index) => (
                <ListItem key={index} className={classes.listItem}>
                  <ListItemText primary={email} />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteEmail(email)}
                      disabled={saving}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default connect(null, { handleError })(NotificationEmailsModal);
